import React, { useState, useEffect } from 'react';
import { gsap } from 'gsap';
import Draggable from 'react-draggable';
import { Button, Card, Flex, Title, Text } from './UIComponents';
import theme from '../styles/theme';
import { useMobile } from '../contexts/MobileContext';

const PATTERN_SIZE = 5; // Larger editor grid

const EditorCell = ({ isAlive, onClick, x, y, playerColor }) => {
  const cellRef = React.useRef(null);
  const { isMobile } = useMobile();
  
  // Add animation effects
  useEffect(() => {
    if (!cellRef.current) return;
    
    if (isAlive) {
      gsap.fromTo(
        cellRef.current,
        { scale: 0.5, opacity: 0.5 },
        { 
          scale: 1, 
          opacity: 1, 
          duration: 0.2, 
          ease: 'back.out(1.7)' 
        }
      );
    }
  }, [isAlive]);

  const handleInteraction = (e) => {
    e.preventDefault(); // Prevent any default behavior
    e.stopPropagation(); // Stop event propagation
    onClick(x, y);
  };
  
  return (
    <div
      ref={cellRef}
      onTouchEnd={handleInteraction}
      onClick={isMobile ? undefined : handleInteraction}
      style={{
        width: 25,
        height: 25,
        backgroundColor: isAlive ? playerColor : 'transparent',
        border: `2px solid ${isAlive ? playerColor : 'rgba(255, 255, 255, 0.2)'}`,
        borderRadius: theme.borders.radius.sm,
        cursor: 'pointer',
        transition: `all ${theme.animation.durations.fast} ${theme.animation.easing.easeOut}`,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        touchAction: 'none', // Prevent default touch actions
        WebkitTapHighlightColor: 'transparent', // Remove tap highlight on iOS
        WebkitTouchCallout: 'none', // Disable callout on long press
        userSelect: 'none', // Prevent text selection
      }}
      onMouseEnter={(e) => {
        if (!isMobile && !isAlive) {
          gsap.to(e.currentTarget, { 
            backgroundColor: `${playerColor}33`,
            borderColor: `${playerColor}66`,
            duration: 0.2
          });
        }
      }}
      onMouseLeave={(e) => {
        if (!isMobile && !isAlive) {
          gsap.to(e.currentTarget, { 
            backgroundColor: 'transparent',
            borderColor: 'rgba(255, 255, 255, 0.2)',
            duration: 0.2
          });
        }
      }}
    />
  );
};

// Array of preset patterns
const PATTERN_PRESETS = [
  {
    name: 'Glider',
    pattern: [
      [0, 0, 1, 0, 0],
      [0, 0, 0, 1, 0],
      [0, 1, 1, 1, 0],
      [0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0]
    ]
  },
  {
    name: 'Blinker',
    pattern: [
      [0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0],
      [0, 1, 1, 1, 0],
      [0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0]
    ]
  },
  {
    name: 'Block',
    pattern: [
      [0, 0, 0, 0, 0],
      [0, 1, 1, 0, 0],
      [0, 1, 1, 0, 0],
      [0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0]
    ]
  },
  {
    name: 'Glider Gun',
    pattern: [
      [0, 0, 0, 0, 1],
      [0, 0, 1, 0, 1],
      [1, 0, 1, 0, 0],
      [0, 1, 1, 0, 0],
      [0, 0, 0, 0, 0]
    ]
  },
];

// Add this preset button style object near the top of the file
const presetButtonStyle = (isSelected, playerColor) => ({
  padding: `${theme.spacing.sm} ${theme.spacing.md}`,
  borderRadius: theme.borders.radius.md,
  fontSize: '0.9rem',
  fontWeight: 'bold',
  transition: 'all 0.3s ease',
  position: 'relative',
  overflow: 'hidden',
  backgroundColor: isSelected ? playerColor : 'transparent',
  color: isSelected ? 'white' : playerColor,
  border: `2px solid ${playerColor}`,
  boxShadow: isSelected ? `0 2px 8px ${playerColor}66` : 'none',
  transform: 'translateY(0)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 4px 12px ${playerColor}66`,
    backgroundColor: isSelected ? playerColor : `${playerColor}11`
  },
  '&:active': {
    transform: 'translateY(1px)',
    boxShadow: `0 2px 4px ${playerColor}66`
  }
});

// Floating panel styles
const floatingPanelStyle = (isMobile, positionInfo, isMinimized) => ({
  position: 'fixed',
  zIndex: isMobile ? positionInfo.zIndex : 1000,
  minWidth: isMobile ? '100%' : '300px',
  maxWidth: isMobile ? '100%' : 'auto',
  backgroundColor: theme.colors.card,
  borderRadius: isMobile 
    ? `${theme.borders.radius.lg} ${theme.borders.radius.lg} 0 0` 
    : theme.borders.radius.lg,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
  border: `1px solid ${theme.colors.border}`,
  cursor: isMobile ? 'default' : 'move',
  userSelect: 'none',
  right: isMobile ? '0' : '20px',
  top: isMobile ? 'auto' : '20px',
  bottom: isMobile ? positionInfo.bottom : 'auto',
  transition: 'bottom 0.3s ease'
});

const dragHandleStyle = (isMobile) => ({
  padding: theme.spacing.sm,
  borderBottom: `1px solid ${theme.colors.border}`,
  borderTopLeftRadius: theme.borders.radius.lg,
  borderTopRightRadius: theme.borders.radius.lg,
  background: `linear-gradient(to right, ${theme.colors.background}, ${theme.colors.card})`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: isMobile ? 'default' : 'move'
});

const ModernEditor = ({ 
  pattern = Array(PATTERN_SIZE).fill().map(() => Array(PATTERN_SIZE).fill(0)), 
  setPattern,
  playerColor = theme.colors.primary,
  onSubmitPattern,
  onTriggerEarthquake,
  error,
  cooldownRemaining = 0,
  earthquakeCooldown = 0
}) => {
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const { isMobile, togglePanel, calculatePanelPosition } = useMobile();
  const editorRef = React.useRef(null);
  
  // Get panel position info
  const positionInfo = calculatePanelPosition('patternEditor');

  // Initialize minimized state based on mobile
  useEffect(() => {
    if (isMobile) {
      setIsMinimized(true);
    }
  }, [isMobile]);

  // Update the context when minimized state changes
  useEffect(() => {
    if (togglePanel) {
      togglePanel('patternEditor', !isMinimized);
    }
  }, [isMinimized]);
  
  // Ensure we always have a valid 5x5 pattern
  useEffect(() => {
    if (!pattern || !Array.isArray(pattern) || pattern.length !== PATTERN_SIZE || !pattern.every(row => Array.isArray(row) && row.length === PATTERN_SIZE)) {
      console.log('Initializing pattern with empty grid');
      setPattern(Array(PATTERN_SIZE).fill().map(() => Array(PATTERN_SIZE).fill(0)));
    }
  }, [pattern, setPattern]);
  
  const toggleCell = (i, j) => {
    if (!pattern || !Array.isArray(pattern)) {
      console.log('Creating new pattern grid');
      const newPattern = Array(PATTERN_SIZE).fill().map(() => Array(PATTERN_SIZE).fill(0));
      newPattern[i][j] = 1;
      setPattern(newPattern);
      return;
    }

    const newPattern = pattern.map(row => [...row]);
    newPattern[i][j] = newPattern[i][j] ? 0 : 1;
    setPattern(newPattern);
  };
  
  const clearPattern = () => {
    setSelectedPreset(null);
    const newPattern = Array(PATTERN_SIZE).fill().map(() => Array(PATTERN_SIZE).fill(0));
    setPattern(newPattern);
    
    // Add "clear" animation
    gsap.fromTo(
      '.editor-cell',
      { backgroundColor: playerColor, scale: 1 },
      { 
        backgroundColor: 'transparent', 
        scale: 0.8,
        stagger: {
          grid: [PATTERN_SIZE, PATTERN_SIZE],
          from: 'center',
          amount: 0.3
        },
        ease: 'power3.out',
        duration: 0.3,
      }
    );
  };
  
  const loadPreset = (preset) => {
    setSelectedPreset(preset.name);
    setPattern(preset.pattern);
    
    // Add "load" animation
    gsap.fromTo(
      '.editor-cell',
      { scale: 0.5, opacity: 0.5 },
      { 
        scale: 1, 
        opacity: 1, 
        stagger: {
          grid: [PATTERN_SIZE, PATTERN_SIZE],
          from: 'center',
          amount: 0.2
        },
        ease: 'back.out(1.7)',
        duration: 0.3,
      }
    );
  };
  
  // Function to randomly generate a pattern
  const generateRandom = () => {
    setSelectedPreset(null);
    const newPattern = Array(PATTERN_SIZE).fill().map(() => 
      Array(PATTERN_SIZE).fill(0).map(() => Math.random() > 0.7 ? 1 : 0)
    );
    setPattern(newPattern);
    
    // Add "random" animation
    gsap.fromTo(
      '.editor-cell',
      { scale: 0.5, opacity: 0.5 },
      { 
        scale: 1, 
        opacity: 1, 
        stagger: {
          grid: [PATTERN_SIZE, PATTERN_SIZE],
          from: 'random',
          amount: 0.5
        },
        ease: 'elastic.out(1, 0.3)',
        duration: 0.5,
      }
    );
  };

  return (
    <Draggable 
      handle=".drag-handle" 
      bounds="body"
      disabled={isMobile}
    >
      <div style={floatingPanelStyle(isMobile, positionInfo, isMinimized)}>
        <div className="drag-handle" style={dragHandleStyle(isMobile)}>
          <Title level={3} style={{ margin: 0, cursor: isMobile ? 'default' : 'move', paddingLeft: theme.spacing.sm }}>Pattern Editor</Title>
          <Button
            size="small"
            onClick={() => setIsMinimized(!isMinimized)}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: playerColor,
              padding: theme.spacing.xs,
              cursor: 'pointer',
              fontSize: '1.2rem',
              lineHeight: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {isMinimized ? '🔽' : '🔼'}
          </Button>
        </div>

        {!isMinimized && (
          <Card style={{ 
            borderTopLeftRadius: 0, 
            borderTopRightRadius: 0,
            cursor: 'default',
            padding: theme.spacing.md
          }}>
            <Flex direction="column" align="center" gap="md">
              <div style={{ 
                position: 'relative'
              }}>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: `repeat(${PATTERN_SIZE}, 25px)`,
                  gridGap: '4px',
                  background: theme.colors.background,
                  padding: theme.spacing.sm,
                  borderRadius: theme.borders.radius.md,
                  boxShadow: theme.borders.shadow.small
                }}>
                  {pattern?.map((row, i) => (
                    row?.map((cell, j) => (
                      <EditorCell
                        key={`${i}-${j}`}
                        isAlive={cell === 1}
                        onClick={toggleCell}
                        x={i}
                        y={j}
                        playerColor={playerColor}
                        className="editor-cell"
                      />
                    ))
                  ))}
                </div>
              </div>
              
              <Flex direction="column" align="center" gap="sm" style={{ width: '100%', position: 'relative' }}>
                {/* Cooldown overlay for pattern buttons area */}
                {cooldownRemaining > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    borderRadius: theme.borders.radius.md,
                    backgroundColor: 'rgba(0, 0, 0, 0.4)',
                    backdropFilter: 'blur(2px)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10,
                    padding: theme.spacing.md
                  }}>
                    <Title level={3} style={{ color: 'white', marginBottom: theme.spacing.sm }}>
                      Cooldown: {cooldownRemaining}s
                    </Title>
                    <div style={{
                      width: '80%',
                      height: '4px',
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      borderRadius: '2px',
                      overflow: 'hidden',
                      position: 'relative'
                    }}>
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        height: '100%',
                        width: `${(5 - cooldownRemaining) * 20}%`,
                        background: `linear-gradient(45deg, ${playerColor}, ${theme.colors.secondary})`,
                        transition: 'width 1s linear'
                      }} />
                    </div>
                  </div>
                )}

                <Flex gap="xs" wrap="wrap" justify="center" style={{ 
                  width: '100%',
                  padding: `${theme.spacing.xs} ${theme.spacing.md}`
                }}>
                  {PATTERN_PRESETS.map((preset) => (
                    <Button 
                      key={preset.name}
                      size="small"
                      onClick={() => loadPreset(preset)}
                      style={{
                        ...presetButtonStyle(selectedPreset === preset.name, playerColor),
                        margin: `${theme.spacing.xs} ${theme.spacing.xs}`,
                        padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                        fontSize: '0.85rem'
                      }}
                    >
                      {preset.name}
                    </Button>
                  ))}
                </Flex>
                <Flex gap="xs" wrap="wrap" justify="center" style={{ 
                  padding: `0 ${theme.spacing.md}`
                }}>
                  <Button 
                    size="small"
                    onClick={generateRandom}
                    style={{
                      ...presetButtonStyle(false, playerColor),
                      backgroundColor: `${playerColor}11`,
                      margin: `${theme.spacing.xs} ${theme.spacing.xs}`,
                      padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                      fontSize: '0.85rem',
                      '&:hover': {
                        backgroundColor: `${playerColor}22`,
                        transform: 'translateY(-2px)',
                        boxShadow: `0 4px 12px ${playerColor}66`
                      }
                    }}
                  >
                    Random
                  </Button>
                  <Button 
                    size="small"
                    onClick={clearPattern}
                    style={{
                      ...presetButtonStyle(false, playerColor),
                      backgroundColor: 'transparent',
                      borderStyle: 'dashed',
                      margin: `${theme.spacing.xs} ${theme.spacing.xs}`,
                      padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                      fontSize: '0.85rem',
                      '&:hover': {
                        backgroundColor: `${playerColor}11`,
                        transform: 'translateY(-2px)',
                        boxShadow: `0 4px 12px ${playerColor}66`
                      }
                    }}
                  >
                    Clear
                  </Button>
                  
                  {/* Earthquake Button */}
                  <Button 
                    size="small"
                    onClick={onTriggerEarthquake}
                    disabled={earthquakeCooldown > 0}
                    style={{
                      padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                      borderRadius: theme.borders.radius.md,
                      fontSize: '0.85rem',
                      fontWeight: 'bold',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      overflow: 'hidden',
                      backgroundColor: earthquakeCooldown > 0 ? 'transparent' : `rgba(211, 47, 47, 0.1)`,
                      color: earthquakeCooldown > 0 ? 'rgba(211, 47, 47, 0.5)' : '#D32F2F',
                      border: `2px solid ${earthquakeCooldown > 0 ? 'rgba(211, 47, 47, 0.3)' : '#D32F2F'}`,
                      boxShadow: 'none',
                      transform: 'translateY(0)',
                      margin: `${theme.spacing.xs} ${theme.spacing.xs}`,
                      cursor: earthquakeCooldown > 0 ? 'not-allowed' : 'pointer',
                      opacity: earthquakeCooldown > 0 ? 0.7 : 1,
                      '&:hover': {
                        backgroundColor: earthquakeCooldown > 0 ? 'transparent' : `rgba(211, 47, 47, 0.2)`,
                        transform: earthquakeCooldown > 0 ? 'none' : 'translateY(-2px)',
                        boxShadow: earthquakeCooldown > 0 ? 'none' : '0 4px 12px rgba(211, 47, 47, 0.3)'
                      },
                      '&:active': {
                        transform: earthquakeCooldown > 0 ? 'none' : 'translateY(1px)',
                        boxShadow: 'none'
                      }
                    }}
                  >
                    {earthquakeCooldown > 0 ? (
                      <span style={{ opacity: 0.7 }}>Cooldown: {earthquakeCooldown}s</span>
                    ) : (
                      <>⚠️ Earthquake!</>
                    )}
                    {earthquakeCooldown > 0 && (
                      <div style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        height: '2px',
                        background: '#D32F2F',
                        width: `${(30 - earthquakeCooldown) * (100/30)}%`,
                        transition: 'width 1s linear'
                      }} />
                    )}
                  </Button>
                </Flex>

                {/* Error display section */}
                {error && (
                  <Text color="error" style={{ 
                    textAlign: 'center',
                    fontSize: '0.85rem',
                    padding: `0 ${theme.spacing.md}`,
                    width: '100%'
                  }}>
                    {error}
                  </Text>
                )}
              </Flex>
            </Flex>
          </Card>
        )}
      </div>
    </Draggable>
  );
};

export default ModernEditor; 